import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, useScroll, useSpring } from "framer-motion";
import { LogoFull } from "@/components/Logo";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/company", label: "Company" },
  { to: "/careers", label: "Careers" },
  { to: "/investors", label: "Investors" },
  { to: "/contact", label: "Contact" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 26 });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar-container"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? "border-b border-line bg-white/90 backdrop-blur-xl" : "bg-white"
      }`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
        <Link to="/" data-testid="nav-link-home-logo" className="shrink-0" aria-label="True Life Entertainment home">
          <LogoFull />
        </Link>

        <div className="hidden items-center gap-8 md:flex">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `group relative font-mono text-[11px] font-semibold uppercase tracking-[0.18em] transition-colors ${
                  isActive || (l.to === "/" && pathname === "/") ? "text-accent" : "text-steel hover:text-charcoal-900"
                }`
              }
            >
              {l.label}
              <span
                className={`absolute -bottom-1.5 left-0 h-px bg-accent transition-all duration-300 ${
                  pathname === l.to ? "w-full" : "w-0 group-hover:w-full"
                }`}
              />
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="nav-cta"
            className="rounded-md bg-accent px-5 py-2.5 font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-white transition-colors duration-200 hover:bg-accent-dark"
          >
            Get in Touch
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="flex h-10 w-10 flex-col items-center justify-center gap-1.5 md:hidden"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          <span className={`h-px w-6 bg-charcoal-900 transition-transform duration-300 ${open ? "translate-y-[3.5px] rotate-45" : ""}`} />
          <span className={`h-px w-6 bg-charcoal-900 transition-transform duration-300 ${open ? "-translate-y-[3px] -rotate-45" : ""}`} />
        </button>
      </nav>

      {open && (
        <div className="border-t border-line bg-white px-6 pb-6 pt-2 md:hidden">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-mobile-${l.label.toLowerCase()}`}
              onClick={() => setOpen(false)}
              className={`block border-b border-line py-3.5 font-display text-base font-semibold ${
                pathname === l.to ? "text-accent" : "text-charcoal-900"
              }`}
            >
              {l.label}
            </Link>
          ))}
          <Link
            to="/contact"
            data-testid="nav-mobile-cta"
            onClick={() => setOpen(false)}
            className="mt-4 block rounded-md bg-accent py-3 text-center font-mono text-xs font-semibold uppercase tracking-[0.15em] text-white"
          >
            Get in Touch
          </Link>
        </div>
      )}

      <motion.div className="absolute bottom-0 left-0 h-[2px] w-full origin-left bg-accent" style={{ scaleX: progress }} />
    </header>
  );
};

export default Navbar;
